import { BoardColumnShell } from '@/components/board/board-column-shell';
import { useDroppable } from '@dnd-kit/core';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { TaskStatusItem } from '@hygge/shared';
import { Plus } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useCreateTask } from '@/hooks/use-tasks';
import { useBoardPermission } from '@/hooks/use-boards';

interface BoardColumnProps {
  projectId: string;
  status: TaskStatusItem;
  count: number;
  children: ReactNode;
}

const CATEGORY_COLORS: Record<string, string> = {
  backlog: '#6B7280',
  active: '#3B82F6',
  done: '#10B981',
  cancelled: '#EF4444',
};

export function BoardColumn({ projectId, status, count, children }: BoardColumnProps) {
  // Both entry points here post a task, which this board answers for with
  // `task.manage`. A Viewer had them offered and refused.
  const canCreate = useBoardPermission(projectId, 'task.manage');
  const { setNodeRef, isOver } = useDroppable({ id: status.id });
  const [isAdding, setIsAdding] = useState(false);

  const handleClose = useCallback(() => {
    setIsAdding(false);
  }, []);

  const categoryColor = CATEGORY_COLORS[status.category] ?? status.color;

  return (
    <BoardColumnShell
      accentColor={categoryColor}
      header={
        <>
          <div
            className="h-2.5 w-2.5 rounded-full shrink-0"
            style={{ backgroundColor: status.color }}
          />
          <span className="font-sans text-[13px] font-medium text-foreground truncate">
            {status.name}
          </span>
          <span className="text-xs text-muted-foreground tabular-nums">{count}</span>
          <div className="flex-1" />
          {canCreate && (
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="h-6 w-6 flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-accent transition-colors cursor-pointer"
                    onClick={() => setIsAdding(true)}
                    aria-label="Add task"
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>Add task</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </>
      }
      bodyRef={setNodeRef}
      bodyClassName={cn(
        'transition-[background-color,box-shadow] duration-200 ease-out',
        isOver && 'bg-accent/40',
      )}
    >
      {children}

      {isAdding ? (
        <InlineAddCard projectId={projectId} statusId={status.id} onClose={handleClose} />
      ) : !canCreate ? null : (
        <button
          type="button"
          className={cn(
            'flex items-center shrink-0 rounded-md text-xs text-muted-foreground hover:text-foreground transition-colors cursor-pointer',
            count === 0
              ? 'justify-center h-20 border border-dashed border-border/50 hover:border-primary/30'
              : 'px-2 py-1.5 hover:bg-muted/40',
          )}
          onClick={() => setIsAdding(true)}
        >
          <Plus className="h-3.5 w-3.5 mr-1" />
          New task
        </button>
      )}
    </BoardColumnShell>
  );
}

function InlineAddCard({
  projectId,
  statusId,
  onClose,
}: {
  projectId: string;
  statusId: string;
  onClose: () => void;
}) {
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const createMutation = useCreateTask(projectId);

  useEffect(() => {
    requestAnimationFrame(() => inputRef.current?.focus());
  }, []);

  const isCreatingRef = useRef(false);

  const handleSubmit = useCallback(async () => {
    const title = value.trim();
    if (!title) return;

    isCreatingRef.current = true;
    setValue('');
    try {
      await createMutation.mutateAsync({ title, statusId });
    } finally {
      isCreatingRef.current = false;
    }
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [value, statusId, createMutation]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
      if (e.key === 'Escape') {
        onClose();
      }
    },
    [handleSubmit, onClose],
  );

  return (
    <div className="rounded-lg border border-primary/30 bg-card p-2 shadow-sm">
      <textarea
        ref={inputRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          setTimeout(() => {
            if (!isCreatingRef.current && !inputRef.current?.matches(':focus')) {
              onClose();
            }
          }, 150);
        }}
        placeholder="Task title..."
        rows={2}
        data-slot="inline-add-task-input"
        className="w-full bg-transparent text-sm text-foreground placeholder:text-muted-foreground/50 resize-none outline-none focus:outline-none focus-visible:outline-none ring-0 focus:ring-0"
      />
      <div className="flex items-center justify-between mt-1">
        <span className="text-[10px] text-muted-foreground">
          Enter to create &middot; Esc to cancel
        </span>
        {createMutation.isPending && (
          <span className="text-[10px] text-muted-foreground animate-pulse">Creating...</span>
        )}
      </div>
    </div>
  );
}
